import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, ScrollView, useWindowDimensions, View } from 'react-native'
import { useModalize } from 'react-native-modalize'
import { Portal } from '@gorhom/portal'

import AddCircularIcon from '@common/assets/svg/AddCircularIcon'
import CloseIcon from '@common/assets/svg/CloseIcon'
import Button from '@common/components/Button'
import Search from '@common/components/Search'
import Text from '@common/components/Text'
import { isWeb } from '@common/config/env'
import useNavigation from '@common/hooks/useNavigation'
import useTheme from '@common/hooks/useTheme'
import Account from '@common/modules/account-select/components/Account'
import AddAccount from '@common/modules/account-select/components/AddAccount'
import {
  AUTH_RADIUS,
  ONBOARDING_CARD_WIDTH
} from '@common/modules/auth/components/OnboardingCard/OnboardingCard'
import { ROUTES } from '@common/modules/router/constants/common'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import text from '@common/styles/utils/text'

import useAccountSelectScreen from './useAccountSelectScreen'

// The dashboard's scrolling column is a RN-Web ScrollView that carries a CSS
// `transform` — and a transformed ancestor becomes the containing block for
// `position: fixed` descendants, trapping this popup inside the sidebar/card
// (painted under the token list, so outside-clicks miss the backdrop too).
//
// We portal to the @gorhom global host (mounted at the AppInit root, outside the
// dashboard's transform) rather than straight to <body>. That still escapes the
// transform trap, but — crucially — it lands the picker in the SAME stacking
// context as the "Add account" BottomSheet (which also lives in that host), so
// their z-indexes are directly comparable. We keep the picker BELOW the bottom
// sheet's z (900): the sheet must paint over the picker AND keep its own backdrop
// below its content (lifting the whole sheet above 900 would push its backdrop
// over its options, swallowing every click). 800 stays above the dashboard (the
// host out-stacks page content) yet under the sheet + its 899 backdrop.
const PICKER_Z_INDEX = 800

type Props = {
  isVisible: boolean
  onClose: () => void
  // Auto-expand the "Add an account" popup as soon as the switcher opens. Used
  // when returning from a backed-out onboarding sub-flow so the user lands back
  // on Add-account rather than the bare account list.
  autoOpenAddAccount?: boolean
}

// Mobile-web account switcher rendered as a real popup overlay (dimmed backdrop
// + centered card) on top of the dashboard, instead of a full-screen route.
// Picking an account closes the popup — the dashboard is already mounted behind
// it, so there is nothing to navigate to. Logic shared with the route screens
// via useAccountSelectScreen; passing onAccountSwitched makes the select close
// the popup rather than navigate to the dashboard.
const AccountSelectModal = ({ isVisible, onClose, autoOpenAddAccount }: Props) => {
  const { theme } = useTheme()
  const { width: windowWidth } = useWindowDimensions()
  const { t } = useTranslation()
  const { navigate } = useNavigation()
  const { ref: sheetRef, open: openBottomSheet, close: closeBottomSheet } = useModalize()
  const {
    accounts,
    control,
    shouldDisplayAccounts,
    onAccountSelect,
    shouldTriggerAddAccountSheetFromSearch
  } = useAccountSelectScreen({ onAccountSwitched: onClose })

  useEffect(() => {
    if (!isVisible || (!shouldTriggerAddAccountSheetFromSearch && !autoOpenAddAccount)) return

    const timeoutId = setTimeout(() => openBottomSheet(), 100)

    return () => clearTimeout(timeoutId)
  }, [isVisible, autoOpenAddAccount, openBottomSheet, shouldTriggerAddAccountSheetFromSearch])

  if (!isVisible) return null

  return (
    <Portal hostName="global">
    <View style={[{ position: 'fixed' as any, inset: 0 as any, zIndex: PICKER_Z_INDEX as any }, flexbox.center]}>
      {/* Dimmed backdrop — clicking outside the card dismisses the popup. */}
      <Pressable
        style={[{ position: 'absolute' as any, inset: 0 as any }, { backgroundColor: 'rgba(0,0,0,0.5)' }]}
        onPress={onClose}
      />

      {/* Centered card. box-none lets clicks on the empty area fall through to
          the backdrop while the card itself stays interactive. */}
      <View style={[flexbox.center, { width: '100%', height: '100%' }]} pointerEvents="box-none">
        <View
          style={{
            width: Math.min(ONBOARDING_CARD_WIDTH, windowWidth * 0.9),
            maxHeight: '90%',
            backgroundColor: theme.onboardingCardBackground,
            borderRadius: AUTH_RADIUS,
            padding: 24,
            shadowColor: theme.onboardingCardShadow,
            shadowOffset: { width: 0, height: 12 },
            shadowOpacity: 1,
            shadowRadius: 32,
            elevation: 12
          }}
        >
          <View style={[flexbox.directionRow, flexbox.alignCenter, spacings.mbLg]}>
            <View style={flexbox.flex1}>
              <Text fontSize={20} weight="semiBold" color={theme.primaryText}>
                {t('Select account')}
              </Text>
            </View>
            <Pressable
              onPress={onClose}
              style={({ hovered }: any) => [
                {
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  backgroundColor: hovered ? theme.tertiaryBackground : theme.secondaryBackground
                },
                flexbox.center
              ]}
            >
              <CloseIcon color={theme.primaryText} width={14} height={14} />
            </Pressable>
          </View>

          <Search
            autoFocus={isWeb}
            control={control}
            style={{ width: '100%', ...spacings.pvSm }}
            containerStyle={spacings.mbSm}
          />

          <ScrollView
            style={{ flexShrink: 1 }}
            contentContainerStyle={{ opacity: shouldDisplayAccounts ? 1 : 0 }}
            showsVerticalScrollIndicator={false}
          >
            {accounts.length ? (
              accounts.map((acc) => (
                <Account
                  key={acc.addr}
                  onSelect={onAccountSelect}
                  account={acc}
                  withSettings={false}
                  options={{ markSelected: true }}
                  withReceive
                  maxAccountAddrLength={32}
                />
              ))
            ) : (
              <View style={[flexbox.center, spacings.pvLg]}>
                <Text style={text.center}>{t('No accounts found')}</Text>
              </View>
            )}
          </ScrollView>

          <View style={spacings.ptSm}>
            <Button
              testID="button-add-account"
              text={t('Add account')}
              size="regular"
              onPress={openBottomSheet as any}
              childrenPosition="left"
              style={{ ...flexbox.alignSelfCenter, width: '100%' }}
            >
              <AddCircularIcon width={24} height={24} color="#fff" style={spacings.mrTy} />
            </Button>
            <Button
              testID="button-sync-to-another-device"
              text={t('Sync to another device')}
              type="ghost"
              size="regular"
              hasBottomSpacing={false}
              onPress={() => {
                onClose()
                navigate(ROUTES.accountsSyncExport)
              }}
              style={{ ...flexbox.alignSelfCenter, ...spacings.mtTy, width: '100%' }}
            />
          </View>
        </View>
      </View>

      <AddAccount sheetRef={sheetRef} closeBottomSheet={closeBottomSheet} />
    </View>
    </Portal>
  )
}

export default React.memo(AccountSelectModal)
